/**
 * RNC — RateLimitPanel
 *
 * Shows the current rate-limit windows. Values are pulled from
 * `config.systemSettings` with documented fallbacks. All values come from the
 * RNC digest §Rate limits:
 *
 *   - Max 5 notifications / customer / 24 h (all channels).
 *   - Max 2 SMS / customer / day.
 *   - Max 1 marketing email / customer / day.
 *
 * This panel is read-only today; the live override button is PLANNED (admin
 * supervisor override path).
 */
import { useConfig } from '@/contexts/ConfigContext';
import { Pill } from '@/components/primitives';

function readNum(
  systemSettings: Record<string, unknown>,
  key: string,
  fallback: number,
): number {
  const v = systemSettings[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number.parseFloat(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

export default function RateLimitPanel(): JSX.Element {
  const { config } = useConfig();
  const s = config.systemSettings;

  const per24h = readNum(s, 'notifications.maxPerCustomer24h', 5);
  const smsPerDay = readNum(s, 'notifications.maxSmsPerCustomerDay', 2);
  const marketingPerDay = readNum(s, 'notifications.maxMarketingPerCustomerDay', 1);

  return (
    <section className="rnc-panel" aria-label="Rate limits">
      <h2 className="rnc-panel__title">Rate limits</h2>
      <div className="rnc-rate-grid">
        <div className="rnc-rate-cell">
          <span className="rnc-rate-cell__label">All channels</span>
          <span className="rnc-rate-cell__value">{per24h}</span>
          <span style={{ fontSize: 11, color: 'var(--ink-600)' }}>per customer / 24h</span>
        </div>
        <div className="rnc-rate-cell">
          <span className="rnc-rate-cell__label">SMS</span>
          <span className="rnc-rate-cell__value">{smsPerDay}</span>
          <span style={{ fontSize: 11, color: 'var(--ink-600)' }}>per customer / day</span>
        </div>
        <div className="rnc-rate-cell">
          <span className="rnc-rate-cell__label">Marketing email</span>
          <span className="rnc-rate-cell__value">{marketingPerDay}</span>
          <span style={{ fontSize: 11, color: 'var(--ink-600)' }}>per customer / day</span>
        </div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>
        <Pill variant="muted">Hard stop at cap</Pill> &nbsp;
        <span>
          Supervisor overrides are logged as <code>notification-rate-limited</code>
          audit events.
        </span>
      </div>
    </section>
  );
}
